import {
  type ContentNode,
  ContentNodeUtils,
  DataTypeConfigUtils,
  type DataTypeLocator,
} from '@helo/cms'
import { If, Value, pipe } from '@helo/utils'

import { type ColorPatternName, Colors } from '../../Colors'
import type { FormatOptions } from '../../Format'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartDataSlice } from '../ChartData'
import {
  type ChartDataFilter,
  type ChartDataSort,
  chartDataOptions,
} from '../ChartData/ChartData.options'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import type { ResolvedChartSeries } from './ChartSeries'
import {
  type ChartVisualization,
  chartSeriesOptions,
} from './ChartSeries.options'

const colorOverridesFromInput = (
  inputSeries: Array<{ color?: string }>,
  resolvedSeries: Array<{ seriesKey: string }>,
): Record<string, string> => {
  const overrides: Record<string, string> = {}
  inputSeries.forEach((inputItem, index) => {
    if (inputItem.color == null) return
    const resolved = resolvedSeries[index]
    if (resolved != null) {
      overrides[resolved.seriesKey] = inputItem.color
    }
  })
  return overrides
}

const assignColors = (
  seriesKeys: string[],
  patternName: ColorPatternName,
  overrides: Record<string, string> = {},
): Record<string, string> =>
  Colors.mapDomain(seriesKeys, Colors.getPattern(patternName), { overrides })
    .byKey

const isVisualization =
  (visualization: ChartVisualization) =>
  (seriesItem: { visualization: ChartVisualization }): boolean =>
    seriesItem.visualization === visualization

const hasLineAndHorizontalBar = (
  series: Array<{ visualization: ChartVisualization }>,
  barOrientation?: string,
): boolean =>
  barOrientation === 'horizontal' &&
  series.some(isVisualization('line')) &&
  series.some(isVisualization('bar'))

const getMeasureState =
  () =>
  (ctx: {
    input: { measureLocator?: DataTypeLocator }
    output: { contentNode?: ContentNode | null }
  }): object => {
    if (ctx.input.measureLocator == null || ctx.output.contentNode == null) {
      return {}
    }
    const root = pipe(
      ChartDataParts.getListContentNode(ctx.output.contentNode),
      ContentNodeUtils.getDataTypeConfig(),
    )
    return pipe(
      root,
      ChartDataParts.findField(ctx.input.measureLocator),
      DataTypeConfigUtils.getState(),
    )
  }

type SeriesInputItem = {
  measureLocator?: DataTypeLocator
  visualization?: ChartVisualization
  label?: string
}

type PivotLongFormatInput = {
  rows: Record<string, unknown>[]
  dimensionKey: string
  seriesByKey: string
  measureKey: string
  measureLocator: DataTypeLocator
  visualization: ChartVisualization
  nullValueBehavior: ChartDataSlice['nullValueBehavior']
  formatDisplayValue: (value: number) => string
}

const pivotLongFormat = ({
  rows,
  dimensionKey,
  seriesByKey,
  measureKey,
  measureLocator,
  visualization,
  formatDisplayValue,
  nullValueBehavior,
}: PivotLongFormatInput): ResolvedChartSeries[] => {
  const xOrder: string[] = []
  const seriesOrder: string[] = []

  for (const row of rows) {
    const x = String(row[dimensionKey] ?? '')
    if (!xOrder.includes(x)) xOrder.push(x)

    const seriesValue = String(row[seriesByKey] ?? '')
    if (!seriesOrder.includes(seriesValue)) seriesOrder.push(seriesValue)
  }

  return seriesOrder.map(seriesValue => {
    const measureByX = new Map<string, unknown>()
    for (const row of rows) {
      if (String(row[seriesByKey] ?? '') !== seriesValue) continue
      measureByX.set(String(row[dimensionKey] ?? ''), row[measureKey])
    }

    const alignedRows = xOrder.map(x => ({
      [dimensionKey]: x,
      [measureKey]: measureByX.has(x) ? measureByX.get(x) : null,
    }))

    return {
      measureLocator,
      visualization,
      seriesKey: seriesValue,
      dataKey: measureKey,
      label: seriesValue,
      formatDisplayValue,
      points: ChartDataParts.normalizePoints(
        alignedRows,
        dimensionKey,
        measureKey,
        nullValueBehavior,
      ),
    }
  })
}

type DeriveSeriesInput = {
  contentNode: ContentNode
  data: ChartDataSlice
  series: ResolvedChartSeries[]
  inputSeries: SeriesInputItem[]
  seriesByLocator?: DataTypeLocator
  dimensionLocator: DataTypeLocator
  dataFilter?: ChartDataFilter
  dataSort: ChartDataSort
}

const deriveSeries = ({
  contentNode,
  data,
  series,
  inputSeries,
  seriesByLocator,
  dimensionLocator,
  dataFilter,
  dataSort,
}: DeriveSeriesInput): ResolvedChartSeries[] => {
  if (seriesByLocator == null) {
    return series
  }

  const firstInputSeries = inputSeries[0]
  const measureLocator = firstInputSeries?.measureLocator
  if (measureLocator == null) {
    throw new Error('ChartSeries requires at least one series measure')
  }

  const listContentNode = ChartDataParts.getListContentNode(contentNode)
  const rootDataTypeConfig = pipe(
    listContentNode,
    ContentNodeUtils.getDataTypeConfig(),
  )
  const seriesByKey =
    ChartDataParts.findFieldKey(seriesByLocator)(rootDataTypeConfig)
  const measureDataTypeConfig =
    ChartDataParts.findField(measureLocator)(rootDataTypeConfig)
  const measureKey = pipe(measureDataTypeConfig, DataTypeConfigUtils.getKey())
  const formatDisplayValue =
    series[0]?.formatDisplayValue ??
    ChartDataParts.createMeasureDisplayFormatter({
      fieldState: pipe(measureDataTypeConfig, DataTypeConfigUtils.getState()),
      seriesOverrides: firstInputSeries,
      rootFormat: {},
    })

  const retrievedRows = pipe(
    listContentNode,
    ContentNodeUtils.getItems(),
    If.isElse(
      Value.isArray,
      items => items as ContentNode[],
      () => [] as ContentNode[],
    ),
  )

  const longRows = retrievedRows.map(rowNode => ({
    [data.dimensionKey]:
      ChartDataParts.valueAtLocator(dimensionLocator)(rowNode),
    [seriesByKey]: ChartDataParts.valueAtLocator(seriesByLocator)(rowNode),
    [measureKey]: ChartDataParts.valueAtLocator(measureLocator)(rowNode),
  }))

  const rows = ChartDataParts.processChartData({
    rows: longRows,
    dimensionKey: data.dimensionKey,
    measureKey,
    dataFilter,
    dataSort,
  })

  return pivotLongFormat({
    rows,
    dimensionKey: data.dimensionKey,
    seriesByKey,
    measureKey,
    measureLocator,
    visualization: firstInputSeries.visualization ?? 'line',
    nullValueBehavior: data.nullValueBehavior,
    formatDisplayValue,
  })
}

const applyTopNPoints = (
  series: ResolvedChartSeries[],
  dataTopN: number | undefined,
): ResolvedChartSeries[] => {
  if (dataTopN == null) return series
  const first = series[0]
  if (first == null) return series
  const keep = new Set(
    [...first.points]
      .filter(point => point.y != null)
      .sort((left, right) => (right.y ?? 0) - (left.y ?? 0))
      .slice(0, Math.max(0, Math.floor(dataTopN)))
      .map(point => point.x),
  )
  return series.map(seriesItem => ({
    ...seriesItem,
    points: seriesItem.points.filter(point => keep.has(point.x)),
  }))
}

type ChartSeriesSetupCtx = {
  input: {
    visualization: ChartVisualization
    measureLocator?: DataTypeLocator
    seriesByLocator?: DataTypeLocator
    dimensionLocator?: DataTypeLocator
    dataFilter?: ChartDataFilter
    dataSort?: ChartDataSort
    dataTopN?: number
    _series?: SeriesInputItem[]
  } & Partial<FormatOptions>
  output: {
    contentNode?: ContentNode | null
    data?: ChartDataSlice
    series?: ResolvedChartSeries[]
  }
}

/** Resolve series and data options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(chartSeriesOptions, ChartDataParts.getChartState()(ctx)),
      Options.fallback(chartDataOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(chartSeriesOptions),
      Options.applyDefaults(chartDataOptions),
      Options.validate(chartSeriesOptions),
    ),
  )

/** Resolve each `_series` item from its measure field state. */
const setup_seriesInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _series: (input._series ?? []).map(series =>
      pipe(
        series,
        Options.fallback(
          chartSeriesOptions,
          getMeasureState()({
            input: series,
            output: ctx.output,
          }),
        ),
        Options.applyDefaults(chartSeriesOptions),
        Options.validate(chartSeriesOptions),
      ),
    ),
  }))

const setSeries = () =>
  RenderPipeline.updateOutput((output: ChartSeriesSetupCtx['output'], ctx) => {
    const contentNode = output.contentNode
    const data = output.data
    if (contentNode == null || data == null) {
      throw new Error('ChartSeries requires Query and ChartData output')
    }

    const inputSeries = ctx.input._series ?? []
    if (inputSeries.length === 0) {
      throw new Error('Chart requires at least one series')
    }

    const listContentNode = ChartDataParts.getListContentNode(contentNode)
    const rootDataTypeConfig = pipe(
      listContentNode,
      ContentNodeUtils.getDataTypeConfig(),
    )
    const rows = data.chartData ?? []
    const rootFormat = ctx.input
    const wideSeries: ResolvedChartSeries[] = inputSeries.map(seriesItem => {
      if (seriesItem.measureLocator == null) {
        throw new Error('Chart series items require a measureLocator')
      }
      const seriesDataTypeConfig = ChartDataParts.findField(
        seriesItem.measureLocator,
      )(rootDataTypeConfig)
      const fieldState = pipe(
        seriesDataTypeConfig,
        DataTypeConfigUtils.getState(),
      )
      const key = ChartDataParts.findFieldKey(seriesItem.measureLocator)(
        rootDataTypeConfig,
      )
      const visualization = seriesItem.visualization ?? ctx.input.visualization
      const label = seriesItem.label ?? ChartDataParts.getFieldLabel(fieldState)
      const formatDisplayValue = ChartDataParts.createMeasureDisplayFormatter({
        fieldState,
        seriesOverrides: seriesItem,
        rootFormat,
      })
      return {
        measureLocator: seriesItem.measureLocator,
        visualization,
        seriesKey: key,
        dataKey: key,
        label,
        formatDisplayValue,
        points: ChartDataParts.normalizePoints(
          rows,
          data.dimensionKey,
          key,
          data.nullValueBehavior,
        ),
      }
    })

    const isPieChart =
      ctx.input.visualization === 'pie' ||
      wideSeries.some(seriesItem => seriesItem.visualization === 'pie')

    const derived = deriveSeries({
      contentNode,
      data,
      series: wideSeries,
      inputSeries,
      seriesByLocator: ctx.input.seriesByLocator,
      dimensionLocator: ctx.input.dimensionLocator ?? data.dimensionLocator,
      dataFilter: ctx.input.dataFilter,
      dataSort: ctx.input.dataSort ?? 'natural',
    })

    const series = isPieChart
      ? derived
      : applyTopNPoints(derived, ctx.input.dataTopN)

    return {
      series,
    }
  })

export const ChartSeriesParts = {
  colorOverridesFromInput,
  assignColors,
  isVisualization,
  hasLineAndHorizontalBar,
  getMeasureState,
  pivotLongFormat,
  deriveSeries,
  applyTopNPoints,
}

export const ChartSeriesInputParts = {
  setupInput,
  setup_seriesInput,
}

export const ChartSeriesOutputParts = {
  setSeries,
}

export const ChartSeriesUtils = ChartSeriesParts
